import { useContext, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  applyCompletion,
  completeAt,
  type CompletionContext,
  type CompletionItem,
  type CompletionResult,
  type ReferenceKind,
} from '../lib/expressions';
import { strings } from '../strings';
import { CompletionSourceContext } from './completion';

export interface ExpressionInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** References offered after `${`. */
  kinds: readonly ReferenceKind[];
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  monospace?: boolean;
  onBlur?: () => void;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
}

const MAX_ITEMS = 50;

/** A text input that completes `${...}` references (combobox pattern). */
export function ExpressionInput({
  id,
  value,
  onChange,
  kinds,
  disabled,
  readOnly,
  required,
  placeholder,
  monospace,
  onBlur,
  ...aria
}: ExpressionInputProps) {
  const source = useContext(CompletionSourceContext);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [completion, setCompletion] = useState<CompletionResult | null>(null);
  const [active, setActive] = useState(0);

  const context = useMemo<CompletionContext>(
    () => ({ kinds, parameters: source.parameters, resources: source.resources, secrets: source.secrets }),
    [kinds, source],
  );

  const update = (text: string, cursor: number) => {
    const result = completeAt(text, cursor, context);
    setCompletion(
      result && result.items.length > 0 ? { ...result, items: result.items.slice(0, MAX_ITEMS) } : null,
    );
    setActive(0);
  };

  const accept = (item: CompletionItem) => {
    const element = input.current;
    if (!element || !completion) return;
    const cursor = element.selectionStart ?? value.length;
    const next = applyCompletion(value, cursor, completion.start, item);
    onChange(next.text);
    setCompletion(null);
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!completion) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => (i + 1) % completion.items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => (i - 1 + completion.items.length) % completion.items.length);
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        accept(completion.items[active]);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setCompletion(null);
        break;
    }
  };

  const optionId = (i: number) => `${listId}-option-${i}`;

  return (
    <div className="relative">
      <input
        ref={input}
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={completion !== null}
        aria-controls={listId}
        aria-activedescendant={completion ? optionId(active) : undefined}
        className={`input ${monospace ? 'font-mono' : ''}`}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
          update(event.target.value, event.target.selectionStart ?? event.target.value.length);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setCompletion(null);
          onBlur?.();
        }}
        {...aria}
      />
      <ul
        id={listId}
        role="listbox"
        aria-label={strings.form.completions}
        hidden={completion === null}
        className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-300 bg-white py-1 text-sm shadow-lg dark:border-slate-600 dark:bg-slate-900"
      >
        {completion?.items.map((item, i) => (
          <li
            key={item.reference}
            id={optionId(i)}
            role="option"
            aria-selected={i === active}
            className={`cursor-pointer px-2 py-1 font-mono ${i === active ? 'bg-blue-100 dark:bg-blue-900' : ''}`}
            onMouseDown={(event) => {
              event.preventDefault();
              accept(item);
            }}
          >
            {item.reference}
            {item.open && (
              <span className="ml-2 font-sans text-xs text-slate-600 dark:text-slate-300">
                ({strings.form.secretCompletion})
              </span>
            )}
            {item.detail && (
              <span className="ml-2 font-sans text-xs text-slate-600 dark:text-slate-300">{item.detail}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
